/* eslint-disable function-name/starts-with-verb */
import type { Experiment, Result } from "@growthbook/growthbook";
import { GrowthbookClientProvider } from "@openfeature/growthbook-client-provider";
import { FirstSuccessfulStrategy, WebMultiProvider } from "@openfeature/multi-provider-web";
import {
  type EvaluationContext,
  type EvaluationContextValue,
  OpenFeature,
  OpenFeatureProvider,
  useFlag,
} from "@openfeature/react-sdk";
import React, { type ReactNode } from "react";
import { type LoaderFunction, type LoaderFunctionArgs, redirect } from "react-router-dom";

import { queryClient } from "@/api";
import { getIndexQueryKey, index } from "@/api/orval/auth/auth";
import { Analytics, type AnalyticsEvents } from "modules/analytics";

import {
  getAllPermissions,
  getAuthToken,
  getPlatformPermissions,
  isAuthorized,
} from "../auth/auth";
import type { UserPermissions } from "../auth/auth";

// Mapped type makes the IDE annotations a bit nicer
export type Flags = { [K in keyof typeof featureFlags]: (typeof featureFlags)[K] };

/**
 * Feature flags and their "fallback" values that are currently available/tracked
 * in the admin web app
 */
const featureFlags = {
  "stripe-risk-review-worklist": false,
  "stripe-payment-account-action-center": false,
  // Tax-document access remains opt-in; the session API independently enforces practice permissions.
  "stripe-tax-documents": false,
  "customer-tax-reconciliation-report": false,
  /**
   * Assume that this flag is always "on" in dev environments, and always "off"
   * in production
   */
  "isDev": false,
  /**
   * Exposes the customer-facing Basic Smart Site domain setup entry point.
   * The API independently enforces role-based access.
   */
  "basic-smart-site-domain-setup": false,
  /**
   * Shows the Customer.io SMS metrics chart on the Platform Overview report.
   */
  "cio-sms-metrics-chart": false,

  /**
   * Controls whether employer-sponsored memberships are exposed to practice staff: the
   * "Sponsors" tab on the App Builder memberships page and the sponsor detail route. Off in code
   * so production stays safe when GrowthBook is disabled or slow; enable per platform in
   * GrowthBook to roll the feature out.
   *
   * UI visibility only — this is NOT a write barrier. `PlatformSponsorsController` carries no
   * flag check, so a Manager or Administrator can still create sponsors, grant sponsor access
   * (which creates a user by phone number), and request removal through the API while the flag is
   * off. If production must not grow sponsor rows before rollout, gate the commands server-side;
   * do not treat this flag as that gate.
   */
  "rmdSponsoredMemberships": false,
  "DeploymentRepeatCash": false,

  /**
   * Enable the referral program
   */
  "reward-patients-for-referring-users": false,
  /*
   * Enable flexible membership terms
   */
  "flexible_membership_terms": false,
  "beautyBankTerm": "Beauty Bank",
  /**
   * Controls whether the import users button is displayed for internal admins only
   */
  "enable-import-users": false,
  /**
   * In-office consult admin UI. Off in code so production stays safe when GrowthBook
   * is disabled or slow; lower envs enable the flag in GrowthBook (default SERVE TRUE).
   */
  "ageless-in-office-consult": false,
  /**
   * Ageless Ads (Meta connection) admin surface. Off in code so production stays safe when
   * GrowthBook is disabled or slow; lower envs enable it in GrowthBook. Access is additionally
   * gated by the `canManageAgelessAds` permission (see core/auth/permissions).
   */
  "agelessMetaAds": false,
  /**
   * Ageless Traffic admin page (T-3472): the customer release lever. Off in code so production
   * stays safe when GrowthBook is disabled or slow. RepeatMD Engineering and RepeatMD Marketing
   * bypass it (see modules/agelessTraffic permissions), so it can stay off in production until
   * customer rollout, which is then a GrowthBook flip with no code change.
   */
  "agelessTraffic": false,
  /**
   * Global switch for the evergreen "Run a Patient Treatment Giveaway" hero on the Ageless
   * Marketing tab (agelessMarketingCampaigns.ts). On in code; turn it off in GrowthBook to hide
   * the hero for every Ageless client at once. Delete the flag together with the hero when the
   * promo is retired.
   */
  "ageless-treatment-giveaway-campaign": true,
  /**
   * Controls whether partial disputes M2 UI (clawback modal, partial lost status) is enabled
   */
  "enablePartialDisputesM2": false,
  /**
   * Controls whether opting into a Done-For-You promo creates a draft Campaign in the Promotions
   * tool instead of the legacy special offers. Only meaningful alongside `plg-discounts`, which
   * grants access to the Promotions tool the resulting draft lands in.
   */
  "dfy-promo-opt-in": false,
  "global-promo-management": true,
  /**
   * Controls whether the automatic payment enforcement feature is displayed
   */
  "paymentEnforcement": false,
  "use-transaction-with-details-table": false,
  /**
   * Controls whether to use the new direct database queries for analytics
   * instead of Snowflake analytics endpoints
   */
  "analytics-use-new-data-source": false,
  "analytics-postgres-refund-amounts-ready": false,
  /**
   * Controls whether the RepeatMD Billing card is shown on Payment & Billing.
   */
  "rmd-billing-container": false,

  /**
   * Controls whether the Ageless Billing & Plan settings tab is visible and
   * whether the underlying route is reachable. While off, the tab is hidden in
   * the Ageless settings header and the billing route redirects to /.
   * Keep disabled until the front-end + back-end billing flow is complete.
   */
  /**
   * Controls whether advanced Ageless billing actions are shown in settings billing
   * (pause/resume, overages, and related non-launch controls).
   */
  "ageless-billing-advanced-actions": false,

  /**
   * Controls whether the Ageless Settings "AI Calling" tab is visible and whether
   * the ai-calling settings route is reachable. Off by default until launch.
   */
  "ageless-ai-calling-settings": false,

  /**
   * Controls whether the Ageless "AI Auto-Reply" opt-in toggle (and its shared
   * practice-knowledge editor) is shown in the AI Outreach settings tab. Keep off
   * until the AI inbound auto-reply feature (email/SMS, PRD06/PRD07) is fully
   * delivered — we don't want to expose the toggle before the feature it gates can
   * send. Distinct from the per-platform PlatformSettingModel.aiAutoReplyEnabled
   * opt-in, which still defaults off independently of this visibility gate.
   */
  "ageless-ai-auto-reply": false,

  /**
   * Controls whether the unified settings menu is enabled
   */
  "unifiedSettingsMenu": false,

  /**
   * Controls whether the dual-shell navigation (separate Global and Platform sidebars) is enabled
   */
  "dual-shell-navigation": true,
  // Eligibility only: each user must also opt in to the product sidebar.
  "role-based-sidebar-ordering": false,
  /**
   * Controls visibility of the integration health dashboard.
   */
  "integration-health-dashboard": false,

  /**
   * Controls whether admins use the new reward discount configuration flow
   */
  "multiple-discounts": false,

  /**
   * Controls whether the new campaign management experience is enabled
   */
  "plg-discounts": false,

  /**
   * Offers "promotional gift card expires after N days" on a buy X get Y offer's complimentary
   * gift card reward in the promotion builder. Off until counsel signs off on the disclosure copy.
   * Not only a UI gate: the API refuses an expiry while the flag is off for the platform.
   */
  "promotional-gift-card-expiry": false,

  /**
   * Controls whether MyProducts shipping configuration and fulfillment management is visible
   */
  "myProductsShipping": false,

  /**
   * Controls whether the Lash & Brow product option is selectable in the Ageless
   * Leads developer tools. Keep off until the Lash & Brow flow ships end-to-end.
   */
  "ageless-leads-lashbrow-option": false,

  /**
   * Controls whether the per-platform "Enable Beautifi" toggle is shown in Admin
   * platform settings. Keep off in production until Beautifi financing ships; also the
   * intended kill-switch for the eventual Beautifi checkout/credential flow. Distinct
   * from the per-platform PlatformSettingModel.beautifiEnabled opt-in.
   */
  "beautifi-platform-toggle": false,

  /**
   * Controls whether the "Checkout tips" section is shown in the App Builder shop
   * settings, letting practices collect tips at checkout. Keep off until the
   * tips-at-checkout feature ships end-to-end.
   */
  "tipsEnabled": false,

  /**
   * Gates the rebuilt membership management experience (full-screen create flow,
   * live preview, versioning/legacy badges). When on, the new
   * MembershipExperienceContainer replaces the drawer-based memberships pages.
   * Keep off in production until the new experience ships.
   */
  "membership-experience-v2": false,

  /**
   * Gates the rebuilt package create/edit experience (single-screen builder with
   * a section rail and live phone preview). When on, the packages list routes to
   * the builder pages instead of opening PackageDrawer for create/edit; the
   * drawer stays the read-only viewer. Keep off in production until it ships.
   */
  "package-experience-v2": false,

  /**
   * Gates the rebuilt treatment builder (full-screen create/edit route with a
   * section rail and live preview). When on, the treatments list pages navigate
   * to the new route instead of opening the TreatmentDrawer.
   * Keep off in production until the new experience ships.
   */
  "treatment-experience-v2": false,

  /**
   * AI promotion generation: the prompt card on the promotions list and the
   * new-promotion builder, plus the builder's "Refine with AI" flow. Off in code
   * so production stays safe when GrowthBook is disabled or slow; lower envs
   * enable it in GrowthBook.
   */
  "agentic-promotion": false,

  /** Gates the draft-only "Refine with AI" flow within the promotion builder. */
  "refine-promotion": false,

  /**
   * Agentic CX Enablement (Practice Actions): the Actions tab on the platform
   * dashboard where CEMs turn meeting notes into reviewable, publishable action
   * items. Internal-only; the backend endpoints carry the same gate and 404
   * while this is off, so the UI flag is never the only barrier.
   */
  "cx-enablement": false,

  /** Controls the Practice Insights tab and CEM filters on platform lists. */
  "practice-insights": false,

  /**
   * Switches the Aesthetix CRM partner on the Partners page from today's Keragon-bridged setup to
   * the direct webhook-URL setup. Off in code so production keeps today's behaviour when
   * GrowthBook is disabled or slow; enable per platform in GrowthBook as Aesthetix onboards
   * practices. Visibility only — `providerType` is a free-form string on the webhook write path,
   * so this flag is not, and must not be treated as, a server-side gate.
   */
  "aesthetix-crm": false,
};

export type FeatureFlagKey = keyof typeof featureFlags;

// Override default values from environment variable for local development
import.meta.env.VITE_ENABLED_FEATURE_FLAGS?.split(",").forEach((flag) => {
  if (typeof featureFlags[flag as keyof Flags] === "boolean") {
    (featureFlags[flag as keyof Flags] as unknown) = true;
  } else {
    console.warn(`Unknown feature flag '${flag}' specified in VITE_ENABLED_FEATURE_FLAGS`);
  }
});

const providers: ConstructorParameters<typeof WebMultiProvider>[0] = [];

if (import.meta.env.VITE_GROWTHBOOK_CLIENT_ID && !import.meta.env.VITEST) {
  providers.push({
    name: "growthbook",
    provider: new GrowthbookClientProvider(
      {
        clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_ID,
        enableDevMode: import.meta.env.DEV,
        trackingCallback: (experiment, result) => {
          Analytics.trackEvent(
            "Experiment Viewed",
            buildExperimentViewedProperties(experiment, result, OpenFeature.getContext()),
          );
        },
      },
      {
        streaming: true,
        timeout: 2000, // important that this is less than the `clientReadyPromise` timeout
      },
    ),
  });
} else {
  console.warn(
    "VITE_GROWTHBOOK_CLIENT_ID variable not provided, feature flags will revert to default values",
  );
}

const clientReady = OpenFeature.setProviderAndWait(
  new WebMultiProvider(providers, new FirstSuccessfulStrategy()),
);
const client = OpenFeature.getClient();

let lastContextChange: Promise<void> = Promise.resolve();
// Auth token the current flag context was identified under. Keying the gate on
// the token (not a boolean) makes a logout → login as a different user in the
// same tab re-identify instead of evaluating flags with the prior user's
// attributes — Admin logout clears the token without reloading the SPA.
let identifiedAuthToken: string | null = null;
let pendingSelfIdentify: Promise<void> | null = null;

const selfIdentifyFlagUser = async (platformId?: string) => {
  const authToken = getAuthToken();
  // Deferred import: the generated platforms module binds `useQuery` from
  // "@tanstack/react-query" at its own evaluation time, and this flags module is loaded during
  // test setup app-wide — a static import here would freeze the module's hooks to the real
  // react-query before a test's interception applies.
  const { getPlatformsGetPlatformQueryKey, platformsGetPlatform } =
    await import("@/api/orval/platforms/platforms");

  // Mirrors the admin layout loader (pages/admin.tsx): platform-scoped
  // permissions when the route has a platformId, otherwise the first
  // permission entry, whose own platformId then selects the platform context.
  // All fetches are query-cache backed, so they dedupe with the layout
  // loader's identical requests running in parallel.
  const [sessionData, permissions] = await Promise.all([
    queryClient.ensureQueryData({
      queryKey: getIndexQueryKey(),
      queryFn: () => index(),
    }),
    platformId ? getPlatformPermissions(platformId) : getAllPermissions().then((all) => all[0]),
  ]);

  const platformIdToLoad = platformId ?? permissions.platformId ?? undefined;
  const platform = platformIdToLoad
    ? await queryClient.ensureQueryData({
        queryKey: getPlatformsGetPlatformQueryKey(platformIdToLoad),
        queryFn: () => platformsGetPlatform(platformIdToLoad),
      })
    : null;

  // A layout loader may have identified this session while we were fetching —
  // its context is at least as specific as ours, so don't overwrite it. And if
  // the session itself changed mid-flight (logout), the fetched data belongs
  // to the previous user; drop it rather than stamping the new session.
  if (identifiedAuthToken === authToken || getAuthToken() !== authToken) return;

  await identifyFlagUser(
    {
      id: permissions.userId,
      name: sessionData.user.name ?? undefined,
      email: sessionData.user.email ?? undefined,
      permissions,
    },
    platform
      ? {
          id: platform.id,
          domain: platform.platformDomains[0]?.domain,
          created: new Date(platform.created),
        }
      : undefined,
  );
};

/**
 * Guarantees the flag user context is identified before a flag is evaluated.
 *
 * Route loaders run in parallel with the layout loaders that call
 * `identifyFlagUser`, so on a fresh page load (deep link, refresh, post-login
 * redirect) `loadIfFlag`/`getFeatureFlag` could otherwise evaluate a
 * user-targeted flag before the user's attributes exist — force rules that
 * target the user (e.g. by email) can then never match, wrongly serving the
 * non-targeted value and redirecting users who are actually targeted.
 *
 * No-op when the current session has already been identified or when
 * unauthenticated (no identity will ever arrive; the layout loader redirects
 * to login).
 */
export const ensureFlagUserIdentified = (platformId?: string): Promise<void> => {
  if (!isAuthorized() || identifiedAuthToken === getAuthToken()) return Promise.resolve();
  pendingSelfIdentify ??= selfIdentifyFlagUser(platformId).finally(() => {
    pendingSelfIdentify = null;
  });
  return pendingSelfIdentify;
};

export const FFlagProvider = (props: { children: ReactNode }) => {
  return <OpenFeatureProvider client={client}>{props.children}</OpenFeatureProvider>;
};

/**
 * Lower-level hooks API to subscribe to the value of a particular feature flag.
 */
export const useFeatureFlag = <Flag extends keyof Flags>(key: Flag) => {
  const { value } = useFlag<Flags[Flag]>(key, featureFlags[key]);
  return value;
};

/**
 * Lower-level API to get the current value of a particular feature flag.
 */
export const getFeatureFlag = async <Flag extends keyof Flags>(key: Flag) => {
  await Promise.all([clientReady, ensureFlagUserIdentified()]);
  await lastContextChange;

  const flagKey = key as unknown as string;
  switch (typeof featureFlags[key]) {
    case "boolean":
      return client.getBooleanValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    case "number":
      return client.getNumberValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    case "string":
      return client.getStringValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    default:
      return client.getObjectValue(flagKey, featureFlags[key]) as Flags[Flag];
  }
};

/**
 * Wrapper component that will render its children only if a feature flag
 * matches the specified value
 *
 * If the `equals` prop is not specified, the children will be rendered if the
 * flag is truthy. Otherwise, they will be rendered only if the flag matches the
 * specified value.
 *
 * @example
 *
 * const MyComponent = () => {
 *   return (
 *     <>
 *       <IfFlag flag="isDev">
 *         <div>This will only be rendered in dev environments</div>
 *       </IfFlag>
 *
 *       <IfFlag flag="isDev" equals={false}>
 *         <div>This will only be rendered in production environments</div>
 *       </IfFlag>
 *    </>
 *   );
 * };
 */
export const IfFlag = <Flag extends keyof Flags>(props: {
  flag: Flag;
  equals?: Flags[Flag];
  children: React.ReactNode;
}) => {
  const flagValue = useFeatureFlag(props.flag);

  if (props.equals === undefined ? flagValue : flagValue === props.equals)
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{props.children}</>;
  else return null;
};

/**
 * Wraps a route loader function to prevent loading the page based on the value
 * of a feature flag.
 *
 * If `equals` argument is not specified, the page will load if the flag is
 * truthy. Otherwise, it will load only if the flag matches the specified value.
 *
 * @example
 *
 * export const loader = loadIfFlag("isDev")(() => {
 *  return { message: "This page will only be reachable in dev environments" };
 * });
 */
export const loadIfFlag =
  <Flag extends keyof typeof featureFlags>(flag: Flag, equals?: (typeof featureFlags)[Flag]) =>
  <Loader extends LoaderFunction>(fn: Loader) => {
    return async (args: LoaderFunctionArgs) => {
      await ensureFlagUserIdentified(args.params.platformId);
      const flagValue = await getFeatureFlag(flag);

      if (equals === undefined ? flagValue : flagValue === equals) {
        return fn(args) as ReturnType<Loader>;
      } else {
        throw redirect("/");
      }
    };
  };

/**
 * Returns the passed object conditionally based on the value of a feature flag.
 *
 * If `equals` argument is not specified, the object will be returned if the
 * flag is truthy. Otherwise, it will be returned only if it matches the
 * specified value.
 *
 * Don't use this in React components, use `<IfFlag />` or `useFeatureFlag()`
 * instead.
 *
 * @example
 *
 * const loader = () => {
 *   const environmentData = await ifFlag("isDev")
 *     ? getForDevelopment()
 *     : getForProduction();
 *
 *   return json(environmentData);
 * };
 */
export const ifFlag =
  <Flag extends keyof typeof featureFlags>(flag: Flag, equals?: (typeof featureFlags)[Flag]) =>
  async <T,>(obj: T): Promise<T | null> => {
    const flagValue = await getFeatureFlag(flag);
    if (equals === undefined ? flagValue : flagValue === equals) return obj;
    else return null;
  };

type UserContext = {
  id: string;
  name?: string;
  email?: string;
  permissions: UserPermissions;
};

type PlatformContext = {
  id: string;
  domain: string;
  created: Date;
};

/**
 * Top-level flag attributes for the practice and the signed-in user. GrowthBook looks up an
 * experiment's hash attribute by exact top-level key, so experiments bucket on these; the nested
 * `platform.targetingKey` / `user.targetingKey` only reach targeting conditions. The names must
 * match the attributes registered in GrowthBook, and are snake_case to match the warehouse
 * identifier types that experiment analysis joins on.
 */
export const PLATFORM_ID_FLAG_ATTRIBUTE = "platform_id";
export const USER_ID_FLAG_ATTRIBUTE = "user_id";

export const buildFlagPlatformContext = (platform: {
  id: string;
  domain?: string;
}): EvaluationContext => ({
  appHost: globalThis.location?.hostname ?? "",
  [PLATFORM_ID_FLAG_ATTRIBUTE]: platform.id,
  platform: {
    targetingKey: platform.id,
    domain: platform.domain ?? null,
  },
});

export const buildFlagUserContext = (
  user: UserContext,
  platform?: PlatformContext,
): EvaluationContext => ({
  appHost: globalThis.location?.hostname ?? "",
  [USER_ID_FLAG_ATTRIBUTE]: user.id,
  user: {
    targetingKey: user.id,
    name: user.name ?? null,
    email: user.email ?? null,
    userRoles: user.permissions.roles,
    isInternalAdmin: user.permissions.isRepeatMDAdministratorUser,
  },
  ...(!!platform && {
    [PLATFORM_ID_FLAG_ATTRIBUTE]: platform.id,
    platform: {
      targetingKey: platform.id,
      domain: platform.domain,
      created: platform.created.toISOString(),
    },
  }),
});

const isIdentifiedFlagUser = (
  value: EvaluationContextValue | undefined,
): value is { isInternalAdmin: boolean } =>
  typeof value === "object" &&
  value !== null &&
  "isInternalAdmin" in value &&
  typeof value.isInternalAdmin === "boolean";

/**
 * `Experiment Viewed` properties for one GrowthBook assignment. Segment snake-cases the keys into
 * the columns GrowthBook's exposure query reads, so renaming one breaks experiment analysis.
 * `isInternalAdmin` lets analyses exclude RepeatMD staff sessions.
 */
export const buildExperimentViewedProperties = (
  experiment: Experiment<unknown>,
  result: Result<unknown>,
  flagContext: EvaluationContext,
): AnalyticsEvents["Experiment Viewed"] => {
  const { user } = flagContext;
  return {
    experimentId: experiment.key,
    variationId: result.key,
    hashAttribute: result.hashAttribute,
    // Typed as a string, but GrowthBook passes the raw attribute value through.
    hashValue: String(result.hashValue),
    ...(isIdentifiedFlagUser(user) && { isInternalAdmin: user.isInternalAdmin }),
  };
};

/**
 * Identify a platform-only flag context, for surfaces that render without a signed-in user
 * (today: the login-free Ageless Marketing page). Platform-targeted rules — which is how
 * Ageless rollouts are scoped — evaluate correctly; user-targeted rules simply do not match,
 * which is the right answer when there is no user. Deliberately does not touch
 * `identifiedAuthToken`: this is not a user identification, and a later sign-in in the same
 * tab must still identify normally.
 */
export const identifyFlagPlatform = (platform: { id: string; domain?: string }) => {
  lastContextChange = OpenFeature.setContext(buildFlagPlatformContext(platform));
  return lastContextChange;
};

/**
 * Identify the current user context for feature flagging
 */
export const identifyFlagUser = (user: UserContext, platform?: PlatformContext) => {
  identifiedAuthToken = getAuthToken();
  lastContextChange = OpenFeature.setContext(buildFlagUserContext(user, platform));
  return lastContextChange;
};
